/**
 * Cross-cadence pricing maths for the public plan cards.
 *
 * A visitor who lands on a 3,990 ₼ number reads "too expensive" and leaves before finding the
 * monthly tab. So monthly leads, and the yearly offer is carried into the monthly cards as a
 * saving ("pay yearly: 66 ₼/month, 2 months free") instead of a bigger price tag. The yearly
 * cards then quote the same per-month number, with the once-a-year total underneath.
 *
 * Everything is derived from the catalogue the API returns — never hardcoded — so a price change
 * in `subscription_plans` moves the landing page with it. Every function returns null rather than
 * a nonsense number when the inputs don't support a claim, and callers render nothing on null.
 */
import { YEARLY_TERM_MONTHS } from "./yearlyTerm";

/** "MONTHLY_STARTER" / "YEARLY_STARTER" -> "STARTER"; the two cadences of one tier share it. */
export function planTier(code) {
  const s = typeof code === "string" ? code : "";
  const i = s.indexOf("_");
  return i === -1 ? s : s.slice(i + 1);
}

/** The yearly price expressed per month — the number a visitor can compare with the monthly plan. */
export function monthlyEquivalent(yearlyPrice) {
  const price = Number(yearlyPrice);
  if (!Number.isFinite(price) || price <= 0) return null;
  return Math.round(price / YEARLY_TERM_MONTHS);
}

/**
 * What paying yearly saves against twelve monthly charges of the same tier.
 * Null when either price is missing or the yearly deal isn't actually cheaper — there is no
 * discount to advertise then, and inventing one would be a lie on the landing page.
 */
export function yearlySaving(monthlyPrice, yearlyPrice) {
  const monthly = Number(monthlyPrice);
  const yearly = Number(yearlyPrice);
  if (!Number.isFinite(monthly) || !Number.isFinite(yearly)) return null;
  if (monthly <= 0 || yearly <= 0) return null;
  const fullYear = monthly * YEARLY_TERM_MONTHS;
  if (yearly >= fullYear) return null;
  const saved = fullYear - yearly;
  return {
    perMonth: monthlyEquivalent(yearly),
    monthlyPrice: monthly,
    fullYear,
    saved,
    percent: Math.round((saved / fullYear) * 100),
    monthsFree: Math.round(saved / monthly),
  };
}

/** Index the catalogue by tier so a card can quote the other cadence's price. */
export function pairByTier(plans) {
  const byTier = {};
  (Array.isArray(plans) ? plans : []).forEach((plan) => {
    if (!plan || !plan.code) return;
    const tier = planTier(plan.code);
    if (!tier) return;
    if (!byTier[tier]) byTier[tier] = {};
    if (plan.billingType === "MONTHLY") byTier[tier].monthly = plan;
    if (plan.billingType === "YEARLY") byTier[tier].yearly = plan;
  });
  return byTier;
}

/**
 * The single saving shown on the billing toggle. Tiers discount identically today (12 months for
 * the price of 10), so the largest percentage across tiers is also the honest one for every tier;
 * if pricing ever diverges this still can't overstate a tier the visitor is looking at, because
 * the per-card lines carry that tier's own number.
 */
export function bestYearlySaving(plans) {
  const pairs = Object.values(pairByTier(plans));
  const savings = pairs
    .map((pair) => yearlySaving(pair.monthly && pair.monthly.price, pair.yearly && pair.yearly.price))
    .filter(Boolean);
  if (savings.length === 0) return null;
  return savings.reduce((best, s) => (s.percent > best.percent ? s : best));
}
